<template>
  <feature
    name="MANAGED_CAMPAIGNS"
  >
    <h3
      data-test="general-label"
    >
      General
    </h3>
    <div class="mb-5">
      <b-row>
        <b-col
          lg="6"
          xl="6"
        >
          <b-form-group
            id="account-manager-set"
          >
            <template
              slot="label"
              label-for="account-manager-set"
              class="form-label"
            >
              <span class="form-label">Account Manager *</span>
            </template>
            <base-autocomplete
              id="account-manager-name"
              v-model="selectedAccountManager"
              :items="accountManagerList"
              type="dropdown"
              :disabled="isCampaignReadonly"
              @change="updateCampaignInfo('accountManagerUserId', selectedAccountManager)"
            />
          </b-form-group>
        </b-col>
        <b-col
          lg="6"
          xl="6"
        >
          <b-form-group
            id="sales-set"
          >
            <template
              slot="label"
              label-for="sales-set"
              class="form-label"
            >
              <span class="form-label">Sales *</span>
            </template>

            <base-autocomplete
              id="sales-name"
              v-model="selectedSales"
              :items="salesList"
              type="dropdown"
              :disabled="isCampaignReadonly"
              @change="updateCampaignInfo('salesUserId',selectedSales)"
            />
          </b-form-group>
        </b-col>
        <b-col
          lg="6"
          xl="6"
        >
          <b-form-group
            id="advertiser-set"
          >
            <template
              slot="label"
              label-for="advertiser-set"
              class="form-label"
            >
              <span class="form-label">Advertiser *
                <span
                  class="form-label-small text-muted"
                >
                  <b-button
                    size="sm"
                    v-b-modal.newAdvertiserModal
                    data-test="new-advertiser-button"
                    v-if="isNewCampaign"
                    variant="secondary"
                  >
                    Add Advertiser
                  </b-button>
                </span>
              </span>
            </template>
            <b-form-select
              :disabled="!isNewCampaign"
              v-model="advertiserSelected"
              :options="getAdvertiserList"
              data-test="campaign-advertiser-select"
              @change="setSelectedAdvertiser"
            />
          </b-form-group>
          <div
            class="alert alert-sm alert-warning"
            data-test="advertiser-alert"
            v-if="isNewCampaign"
          >
            Advertiser name cannot be changed once you exit this page.
          </div>
        </b-col>
        <b-col
          lg="6"
          xl="6"
        >
          <b-form-group
            id="agency-set"
          >
            <template
              slot="label"
              label-for="agency-set"
              class="form-label"
            >
              <span class="form-label">Agency *
                <span
                  class="form-label-small text-muted"
                >
                  <b-button
                    size="sm"
                    v-b-modal.newAgencyModal
                    data-test="new-agency-button"
                    variant="secondary"
                    :disabled="isCampaignReadonly"
                  >
                    Add Agency
                  </b-button>
                </span>
              </span>
            </template>
            <b-form-select
              v-model="agencySelected"
              :options="getAgencyList"
              data-test="campaign-agency-select"
              :disabled="isCampaignReadonly"
              @change="setSelectedAgency"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </div>
    <b-modal
      id="newAdvertiserModal"
      ref="newAdvertisermodal"
      title="Add A New Advertiser"
      @show="resetModal"
      @hidden="resetModal"
      @ok="handleOk"
    >
      <form
        ref="form"
        @submit.stop.prevent="handleSubmit"
      >
        <b-form-group
          label="Advertiser Name"
          label-for="newAdvertiserModal"
        >
          <base-autocomplete
            id="advertiser-name"
            v-model="advertiserName"
            :items="adxAdvertisers"
            :wait-length="3"
          />
        </b-form-group>
      </form>
    </b-modal>
    <b-modal
      id="newAgencyModal"
      ref="newAgencyModal"
      title="Add A New Agency"
      @show="resetAgencyModal"
      @hidden="resetAgencyModal"
      @ok="handleAgencyOk"
    >
      <form
        ref="form"
        @submit.stop.prevent="handleAgencySubmit"
      >
        <b-form-group
          label="Agency Name"
          label-for="newAgencyModal"
        >
          <b-form-input
            id="agency-name"
            v-model="agencyName"
            trim
            data-test="agency-name-input"
          />
        </b-form-group>
      </form>
    </b-modal>
  </feature>
</template>

<script>
import {
  mapState, mapGetters, mapActions, mapMutations,
} from 'vuex';
import * as types from '@/modules/campaigns/store/types';
import * as accountTypes from '@/modules/account/store/types';
import BaseAutocomplete from '@/components/form/BaseAutocomplete.vue';

export default {
  name: 'GeneralSection',
  components: {
    BaseAutocomplete,
  },
  props: {
    isManagedCampaign: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      advertiserSelected: -1,
      agencySelected: -1,
      advertiserName: '',
      newAdvertiserName: '',
      agencyName: '',
      newAgencyName: '',
      selectedAccountManager: {},
      accountManagerList: [],
      selectedSales: {},
      salesList: [],
      agencyList: [],
    };
  },
  computed: {
    ...mapState('campaigns', [
      'adxAdvertisers',
      'selectedAdvertiser',
      'existingAdvertisers',
      'campaign',
    ]),
    ...mapGetters('campaigns', {
      advertiserList: types.GET_ADVERTISER_LIST,
      isCampaignReadonly: types.GET_IS_CAMPAIGN_READONLY,
    }),
    getAdvertiserList() {
      return (this.newAdvertiserName !== '')
        ? [{ value: -1, text: this.newAdvertiserName }, ...this.advertiserList]
        : this.advertiserList;
    },
    getAgencyList() {
      const agencyList = this.agencyList
        .map((item) => ({ value: item.id, text: item.name }));
      return (this.newAgencyName !== '')
        ? [{ value: -1, text: this.newAgencyName }, ...agencyList]
        : agencyList;
    },
    getAdvertiserSet() {
      const advertiserSet = this.getAdvertiserList
        .find((item) => item.value === this.advertiserSelected);
      if (advertiserSet) {
        return {
          id: advertiserSet.value,
          name: advertiserSet.text,
          accountId: advertiserSet.accountId,
        };
      }
      return {
        id: this.advertiserSelected,
        name: this.newAdvertiserName,
        accountId: null,
      };
    },
    getAgencySet() {
      const agencySet = this.getAgencyList
        .find((item) => item.value === this.agencySelected);
      if (agencySet) {
        return {
          id: agencySet.value,
          name: agencySet.text,
        };
      }
      return {
        id: this.agencySelected,
        name: this.newAgencyName,
      };
    },
    isNewCampaign() {
      return Boolean(!this.campaign?.id);
    },
  },
  methods: {
    ...mapActions('account', [
      accountTypes.FETCH_INVOICING_DETAILS,
      accountTypes.FETCH_ACCOUNT_BY_ID,
      accountTypes.FETCH_ALL_USERS_IN_ROLE,
    ]),
    ...mapActions('campaigns', [
      types.UPDATE_SELECTED_ADVERTISER,
      types.FETCH_AGENCIES,
    ]),
    ...mapMutations('account', [
      accountTypes.RESET_INVOICING_DETAILS,
    ]),
    resetModal() {
      this.advertiserName = '';
    },
    handleOk(bvModalEvt) {
      bvModalEvt.preventDefault();
      this.handleSubmit();
    },
    handleSubmit() {
      this.newAdvertiserName = this.advertiserName.trim();
      this.advertiserSelected = -1;
      this.$nextTick(() => {
        this.$bvModal.hide('newAdvertiserModal');
        this.setSelectedAdvertiser();
      });
    },
    resetAgencyModal() {
      this.agencyName = '';
    },
    handleAgencyOk(bvModalEvt) {
      bvModalEvt.preventDefault();
      this.handleAgencySubmit();
    },
    handleAgencySubmit() {
      this.newAgencyName = this.agencyName.trim();
      this.agencySelected = -1;
      this.$nextTick(() => {
        this.$bvModal.hide('newAgencyModal');
        this.setSelectedAgency();
      });
    },
    setSelectedAdvertiser() {
      this.UPDATE_SELECTED_ADVERTISER(this.getAdvertiserSet);
      this.$emit('updateAdvertiser', this.getAdvertiserSet);
    },
    setSelectedAgency() {
      this.$emit('updateAgency', this.getAgencySet);
    },
    getAllUsersFor(role, refList, setMethod) {
      this.FETCH_ALL_USERS_IN_ROLE(role)
        .then((users) => {
          if (!users) return;
          this[refList] = users.userResponses
            .map((user) => ({
              name: `${user.firstName} ${user.lastName}`,
              id: user.id,
            }));
          this[setMethod]();
        });
    },
    updateCampaignInfo(property, value) {
      this.$emit('updateCampaignInfo', property, value.id);
    },
    setSelectedAccountManager() {
      if (this.campaign.accountManagerUserId) {
        const selectedAccountManagerItem = this.accountManagerList
          .find((item) => item.id === this.campaign.accountManagerUserId);
        if (selectedAccountManagerItem) {
          this.selectedAccountManager = selectedAccountManagerItem;
        }
      }
    },
    setSelectedSales() {
      if (this.campaign.salesUserId) {
        const selectedSalesItem = this.salesList
          .find((item) => item.id === this.campaign.salesUserId);
        if (selectedSalesItem) {
          this.selectedSales = selectedSalesItem;
        }
      }
    },
  },
  created() {
    this.FETCH_AGENCIES()
      .then((agencies) => {
        this.agencyList = agencies;
        const { campaign } = this;
        const defaultAgencySet = this.getAgencyList.find((item) => item.text === 'NONE');
        let agencySet = null;
        if (campaign?.agencyId == null) this.agencySelected = defaultAgencySet.value;
        else {
          agencySet = this.getAgencyList.find((item) => item.value === campaign.agencyId);
          agencySet = agencySet || defaultAgencySet;
          this.agencySelected = agencySet.value;
        }
      });
    if (this.selectedAdvertiser !== {}) this.advertiserSelected = this.selectedAdvertiser.id;
    this.setSelectedAdvertiser();
    this.getAllUsersFor('ACCOUNT_MANAGER', 'accountManagerList', 'setSelectedAccountManager');
    this.getAllUsersFor('SALES', 'salesList', 'setSelectedSales');
  },
};
</script>
